import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../../lib/api";
import { SEVERITY_LABELS, SEVERITY_ORDER, describeExtra, pageSummary, subjectLink, subjectName, totalFindings } from "../../lib/consistency";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AlertCircle, CheckCircle2, Info, Loader2, RefreshCw } from "lucide-react";

const ALL = "__all__";
const TONE = {
  action: { pill: "bg-red-50 text-red-700 border-red-200", tile: "border-red-200" },
  check: { pill: "bg-amber-50 text-amber-700 border-amber-200", tile: "border-amber-200" },
  info: { pill: "bg-slate-100 text-slate-600 border-slate-200", tile: "border-slate-200" },
};

const formatTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

// Administrator's read-only consistency report of the organisation (nothing here writes): counts are exhaustive, the list is paged.
export default function ConsistencyPanel() {
  const [severity, setSeverity] = useState(null);
  const [code, setCode] = useState(null);
  const [state, setState] = useState({ status: "loading", data: null, items: [] });
  const [more, setMore] = useState(false);
  const [stale, setStale] = useState(false);
  const request = useRef(0);

  const load = useCallback(async (cursor) => {
    const mine = ++request.current;
    if (!cursor) setStale(false);
    if (cursor) setMore(true);
    else setState((s) => ({ ...s, status: s.data ? "refreshing" : "loading" }));
    try {
      const { data } = await api.get("/collaborators/consistency", { params: { ...(severity ? { severity } : {}), ...(code ? { code } : {}), ...(cursor ? { cursor } : {}) } });
      if (mine !== request.current) return; // a newer filter was chosen meanwhile
      setState((s) => ({ status: "ready", data, items: cursor ? [...s.items, ...data.items] : data.items }));
    } catch (e) {
      if (mine !== request.current) return;
      if (cursor && e?.response?.status === 409) setStale(true); // the organisation changed while the pages were being read
      else setState((s) => ({ ...s, status: "error", message: errorMessage(e) }));
    } finally {
      if (mine === request.current) setMore(false);
    }
  }, [severity, code]);

  useEffect(() => { load(null); }, [load]);

  const { data, items } = state;
  if (state.status === "loading") return <div className="space-y-3" data-testid="consistency-loading"><Skeleton className="h-20 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (state.status === "error" && !data) {
    return (
      <Card className="p-8 text-center space-y-3" data-testid="consistency-error">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Impossible de calculer le contrôle. Réessayez dans quelques instants.</p>
        <Button variant="outline" onClick={() => load(null)}>Réessayer</Button>
      </Card>
    );
  }

  const total = totalFindings(data.counts);
  const perCode = data.counts.per_code;
  const filtered = !!(severity || code);
  const ruleOptions = data.rules.filter((r) => (!severity || r.severity === severity || (perCode[r.code] || 0) > 0) && (perCode[r.code] || 0) > 0);

  return (
    <div className="space-y-4" data-testid="consistency-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {SEVERITY_ORDER.map((level) => {
          const count = data.counts.per_severity[level] || 0;
          const active = severity === level;
          return (
            <button key={level} type="button" aria-pressed={active} data-testid={`consistency-tile-${level}`}
              onClick={() => { setSeverity(active ? null : level); setCode(null); }}
              className={`text-left rounded-xl border bg-white px-4 py-3 transition hover:bg-slate-50 min-h-[44px] ${TONE[level].tile} ${active ? "ring-2 ring-jamora" : ""}`}>
              <div className="text-xs text-slate-500">{SEVERITY_LABELS[level]}</div>
              <div className="font-display font-bold text-2xl text-slate-900" data-testid={`consistency-count-${level}`}>{count}</div>
            </button>
          );
        })}
      </div>

      {!data.top_level_declared && (
        <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="consistency-no-top-notice">
          <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
          <span>Aucun sommet de l'organisation n'est encore déclaré : les fiches sans responsable ne sont signalées qu'à titre d'information. Déclarez le sommet depuis la fiche de chaque dirigeant.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 w-full sm:w-80">
          <Select value={code || ALL} onValueChange={(v) => setCode(v === ALL ? null : v)}>
            <SelectTrigger data-testid="consistency-code" aria-label="Type de constat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les types de constat</SelectItem>
              {ruleOptions.map((r) => <SelectItem key={r.code} value={r.code}>{r.title} ({perCode[r.code]})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {filtered && <Button variant="ghost" size="sm" onClick={() => { setSeverity(null); setCode(null); }} data-testid="consistency-reset">Voir tous les constats</Button>}
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span data-testid="consistency-generated">Calculé le {formatTime(data.generated_at)}</span>
          <Button variant="outline" size="sm" onClick={() => load(null)} disabled={state.status === "refreshing"} data-testid="consistency-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${state.status === "refreshing" ? "animate-spin" : ""}`} />Actualiser
          </Button>
        </div>
      </div>

      {total === 0 ? (
        <Card className="p-8 text-center space-y-2" data-testid="consistency-empty">
          <CheckCircle2 className="mx-auto text-emerald-500" />
          <p className="font-medium text-slate-900">Aucune anomalie dans l'organisation</p>
          <p className="text-sm text-slate-500">Responsables, services et comptes sont cohérents. Ce contrôle se recalcule à chaque ouverture.</p>
        </Card>
      ) : data.total_items === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500" data-testid="consistency-none-for-filter">Aucun constat pour ce filtre.</Card>
      ) : (
        <Card className="divide-y divide-slate-100" data-testid="consistency-list">
          <div className="px-4 py-2 text-xs text-slate-500" data-testid="consistency-summary">{pageSummary(items.length, data.total_items)}</div>
          {items.map((item) => {
            const link = subjectLink(item);
            const extra = describeExtra(item);
            return (
              <div key={`${item.code}-${item.subject_id}`} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`finding-${item.code}`}>
                <span className={`shrink-0 self-start rounded-full border px-2 py-0.5 text-2xs font-medium ${TONE[item.severity].pill}`}>{SEVERITY_LABELS[item.severity]}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900">{item.title}</div>
                  <div className="text-xs text-slate-500 truncate">{subjectName(item)}{extra ? ` · ${extra}` : ""}</div>
                </div>
                {link && <Link to={link.to} state={link.state} className="text-sm text-jamora hover:underline shrink-0 min-h-[44px] sm:min-h-0 flex items-center" data-testid="finding-open">Ouvrir</Link>}
              </div>
            );
          })}
          {stale && (
            <div className="flex flex-col gap-2 border-t border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:flex-row sm:items-center sm:gap-3" role="alert" data-testid="consistency-stale">
              <span className="flex-1 min-w-0">L'organisation a changé depuis l'affichage de la première page : la suite risquerait d'être incomplète.</span>
              <Button size="sm" variant="outline" className="self-start sm:self-auto" onClick={() => load(null)} data-testid="consistency-stale-refresh">Actualiser le contrôle</Button>
            </div>
          )}
          {data.has_more && !stale && (
            <div className="p-3 text-center">
              <Button variant="outline" onClick={() => load(data.next_cursor)} disabled={more} data-testid="consistency-more">
                {more && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger la suite
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
